import { useTranslation } from 'react-i18next';
import { Alert, Breakpoint, Button, ButtonType, Typography, UiComponentSize } from 'app/component-library-wave';

import { navigationService } from 'app/service/navigation/navigation-service';
import { useAppNavigation } from 'app/utils/navigation-utils';
import { InactiveCustomerLocationPage } from 'app/pages/page-wrapper';
import { useAppSelector } from 'app/hooks/redux-thunk';
import { FetchStatus } from 'app/store/root-types';

import { ContainerFixed } from 'app/components/container';
import { HiddenH1 } from 'app/components/hidden-h1';
import { Spinner } from 'app/components/spinner';
import { UserView } from './user-view';
import { useUserInfo } from 'app/hooks/use-users-info';

import styles from './users-page.module.scss';
import { getDataLayers } from 'app/utils/trackingService';
import {
  getAnonymizedCurrentPageUrl,
  getGtmFullUrl,
  GtmElementCategory,
  GtmElementCta,
} from 'app/utils/gtm-tracking-utils';

export const UsersPage: InactiveCustomerLocationPage = ({ userContext }) => {
  const { t } = useTranslation();
  const crmId = useAppSelector((state) => state.userContext.selectedCustomerLocation?.crmId);
  const usersFetchStatus = useAppSelector((state) => state.users.fetchStatus);
  const { goToPath } = useAppNavigation();
  const { users } = useUserInfo(userContext);
  const myUser = (users || []).find((user) => user.id === crmId);
  const otherUsers = (users || []).filter((user) => user.id !== crmId);
  const isPrimaryOrAdmin = myUser?.role ? ['PRIMARY', 'ADMIN'].includes(myUser.role) : false;

  const handleCreateUser = () => {
    getDataLayers().customEvents.pushButtonClickEvent({
      element_category: GtmElementCategory.USERS,
      element_cta: GtmElementCta.USERS_CREATE_USER,
      element_url: getGtmFullUrl(navigationService.PAGES.userCreate.url),
      page_url: getAnonymizedCurrentPageUrl(),
    });

    goToPath(navigationService.PAGES.userCreate.url);
  };

  return (
    <ContainerFixed isNarrow={true} className={styles.usersPage}>
      <HiddenH1>{t(navigationService.PAGES.users.i18n)}</HiddenH1>
      <>
        <div className={styles.banner}>
          <Typography component="h2" variant="headline4" maxBreakpoint={Breakpoint.TABLET} className={styles.heading}>
            {t('pages.users.heading')}
          </Typography>
          <Typography component="p" variant="paragraph1" maxBreakpoint={Breakpoint.TABLET}>
            {t('pages.users.description')}
          </Typography>
        </div>
        {usersFetchStatus === FetchStatus.PENDING && <Spinner />}
        {(usersFetchStatus === FetchStatus.REJECTED || (usersFetchStatus === FetchStatus.FULFILLED && !users)) && (
          <Alert
            alertType="warning"
            heading={t('pages.users.errorMessages.fetchingFailed')}
            headingElement="strong"
            isExpandable={false}
            role="alert"
          />
        )}
        {usersFetchStatus === FetchStatus.FULFILLED && (
          <>
            {myUser && (
              <>
                <Typography
                  component="h2"
                  variant="headline4"
                  maxBreakpoint={Breakpoint.TABLET}
                  className={styles.sectionHeader}
                >
                  {t('pages.users.sections.mainUser')}
                </Typography>
                <UserView user={myUser} canChangePassword={true} canEdit={true} />
              </>
            )}
            {otherUsers[0] && (
              <>
                <Typography
                  component="h2"
                  variant="headline4"
                  maxBreakpoint={Breakpoint.TABLET}
                  className={styles.sectionHeader}
                >
                  {t('pages.users.sections.otherUsers')}
                </Typography>
                <ul className={styles.otherUsersList}>
                  {otherUsers.map((user) => (
                    <li key={user.id} className={styles.otherUser}>
                      <UserView user={user} canDelete={isPrimaryOrAdmin && user.role !== 'PRIMARY'} />
                    </li>
                  ))}
                </ul>
              </>
            )}
            {isPrimaryOrAdmin && (
              <div className={styles.buttonBar}>
                <Button
                  buttonType={ButtonType.SECONDARY}
                  uiComponentSize={UiComponentSize.LARGE}
                  onClick={handleCreateUser}
                >
                  {t('pages.users.buttons.createUser.name')}
                </Button>
              </div>
            )}
          </>
        )}
      </>
    </ContainerFixed>
  );
};
